import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HttpService } from '../../shared/services/http.service';
import { DeviceExecutionCommand, DeviceId, DeviceMS, DevicePairingRequest } from '../models/Device';
import { Device } from '../models/DeviceDetail';
import { Execution } from '../models/Execution';
import { UiProfile } from '../models/UiProfile';
import { UpdateDeviceCommand } from '../models/UpdateDeviceCommand';
import { buildDeviceFromRawDevice } from '../utils/devices';

import { DeviceGateway } from './DeviceGateway';

@Injectable({ providedIn: 'root' })
export class HttpDeviceGateway implements DeviceGateway {
    private readonly httpService = inject(HttpService);
    private readonly http = inject(HttpClient);

    fetchDevice(deviceId: DeviceId): Observable<Device | null> {
        return this.httpService.get('/v1/device/' + deviceId).pipe(
            map((rawDevice: DeviceMS) => buildDeviceFromRawDevice(rawDevice, deviceId)),
            catchError(() => of(null)),
        );
    }

    updateDevice({ device, property, newValue, sessionId }: UpdateDeviceCommand): Observable<Execution> {
        return this.httpService.patch('/v1/device', {
            device,
            command: { stateName: property.id, value: newValue },
            sessionId,
        });
    }

    refreshDeviceStates(deviceId: DeviceId, states: string[]): Observable<Execution[]> {
        return this.httpService.patch(`/v1/device/${deviceId}/refresh`, states);
    }

    getDeviceUI(deviceName: string): Observable<UiProfile> {
        return this.http
            .get<UiProfile>(`./assets/definitionsUI/${deviceName}.json`)
            .pipe(catchError(() => of({} as UiProfile)));
    }

    getDeviceById(idDevice: string): Observable<DeviceMS> {
        return this.httpService.get('/v1/device/' + idDevice);
    }

    pairGatewayDevices(req: DevicePairingRequest): Observable<unknown> {
        return this.httpService.patch('/v1/device/pairRemote', req);
    }

    executeCommand(deviceId: DeviceId, bodyReq: DeviceExecutionCommand): Observable<Execution> {
        return this.httpService.patch(`/v1/device/${deviceId}/exec`, {
            ...bodyReq,
        });
    }
}
